import { Collection, Database, Q } from '@nozbe/watermelondb';

import Organisation from './Organisation';
import { LocationModel, LocationPayload } from '../../types/Location';
import { DBServiceOptionsWithImages } from '../../types/dbService';
import { DatabaseValidation } from '../../types/validation';
import {
    addPreparedDataBatch,
    mapRecord,
    PreparedLocationData,
} from './helpers/location/common';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';
import {
    logAddLocationAction,
    prepareCreateLocation,
} from './helpers/location/locationAdd';
import {
    logDeleteLocationAction,
    prepareDeleteLocation,
} from './helpers/location/locationDelete';
import {
    logUpdateLocationAction,
    prepareUpdateLocation,
} from './helpers/location/locationUpdate';

type LocationServiceOptions = DBServiceOptionsWithImages & {
    validation: DatabaseValidation;
};

class Location {
    private database: Database;
    private collection: Collection<LocationModel>;
    private table = 'locations';
    private options: LocationServiceOptions;

    constructor(options: LocationServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
    }

    getAll() {
        return this.collection.query().fetch();
    }

    getByID(id: string) {
        return this.collection.find(id);
    }

    getByParam(param: string, value: any) {
        return this.collection.query(Q.where(param, value)).fetch();
    }

    async add(payload: LocationPayload, userId: string) {
        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logDBAction,
            validation: this.options.validation,
        });

        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];

        await this.database.write(async () => {
            const preparedData: PreparedLocationData[] = [];
            const createdLocation = prepareCreateLocation({
                payload,
                userId,
                organisationID,
                collection: this.collection,
            });
            preparedData.push(mapRecord(createdLocation, 'locations', 'add'));

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logAddLocationAction({
                location: createdLocation,
                logDBAction: this.options.logDBAction,
                message: 'Create location',
                tableName: this.table,
            });
        });
    }

    async addUnique(payload: LocationPayload | undefined, userId: string) {
        if (!payload?.placeId) return;

        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logDBAction,
            validation: this.options.validation,
        });

        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];

        // do not add the location if already exists
        const existingLocation = await this.getByParam(
            'place_id',
            payload.placeId,
        );
        if (existingLocation?.length > 0) return existingLocation[0];

        let locationElement: LocationModel | undefined = undefined;

        await this.database.write(async () => {
            const preparedData: PreparedLocationData[] = [];
            locationElement = prepareCreateLocation({
                payload,
                userId,
                organisationID,
                collection: this.collection,
            });
            preparedData.push(mapRecord(locationElement, 'locations', 'add'));

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logAddLocationAction({
                location: locationElement,
                logDBAction: this.options.logDBAction,
                message: 'Created unique location',
                tableName: this.table,
            });
        });

        return locationElement;
    }

    async prepareAddUnique(
        payload: LocationPayload | undefined,
        userId: string,
    ) {
        if (!payload?.placeId) return;

        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logDBAction,
            validation: this.options.validation,
        });

        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];

        // do not add the location if already exists
        const existingLocation = await this.getByParam(
            'place_id',
            payload.placeId,
        );
        if (existingLocation?.length > 0)
            return { existingLocation: existingLocation[0] };

        const preparedLocation = await this.collection.prepareCreate(
            (location) => {
                location.placeId = payload.placeId;
                location.name = payload.name;
                location.lat = `${payload.lat}`;
                location.lng = `${payload.lng}`;
                location.url = payload.url;
                location.utcOffset = payload.utcOffset;
                location.userId = userId;
                location.organisationId = organisationID;
            },
        );

        return { preparedLocation };
    }

    async update(id: string, payload: LocationPayload) {
        const locationElement = await this.getByID(id);

        await this.database.write(async () => {
            const preparedData: PreparedLocationData[] = [];
            const updatedLocation = prepareUpdateLocation({
                locationElement,
                payload,
            });
            preparedData.push(
                mapRecord(updatedLocation, 'locations', 'update'),
            );

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logUpdateLocationAction({
                location: updatedLocation,
                logDBAction: this.options.logDBAction,
                tableName: this.table,
            });
        });
    }

    async deleteByID(id: string) {
        const locationElement = await this.getByID(id);

        await this.database.write(async () => {
            const preparedData: PreparedLocationData[] = [];
            const deletedLocation = prepareDeleteLocation({ locationElement });
            preparedData.push(
                mapRecord(deletedLocation, 'locations', 'delete'),
            );

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logDeleteLocationAction({
                location: deletedLocation,
                logDBAction: this.options.logDBAction,
                tableName: this.table,
            });
        });
    }
}

export default Location;
